import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Dialog, DialogContent, IconButton, ToggleButton, ToggleButtonGroup } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import html2canvas from 'html2canvas';
import JsPDF from 'jspdf';
import { ReactComponent as DownloadIcon } from '../../../public/assets/downloadIcon.svg';
import { ReactComponent as ShareIcon } from '../../../public/assets/shareIcon.svg';
import UjnScanAndPayCard from './UjnQRCard';
import IobScanAndPayCard from './IOBQRCard';
import { APP_CONSTANTS } from '../../URLConstants';
import CbiScanAndPayCard from './CBIQRCard';
import SbiScanAndPayCard from './SBIScanAndPayCard';
import CosmosScanAndPayCard from './CosmosQRCard';
import PratapScanAndPayCard from './PratapQRCard';
import RsbcanAndPayCard from './RSBQRCard';
import GbcbScanAndPayCard from './GBCBQRCard';
import IobNewScanAndPayCard from './IOBNewQRCard';
import CbiNewScanAndPayCard from './CBINewQRCard';
import KagbNewScanAndPayCard from './KAGBNewQRCard';
import RsbNewScanAndPayCard from './RSBNewQRCard';
import HastiNewScanAndPayCard from './HASTINewQRCard';

const MODAL_WIDTH_CONFIG = {
  DEFAULT: { minWidth: '400px', maxWidth: '600px' },
  [APP_CONSTANTS?.GBCB_CLIENT_IDENTIFIER]: { minWidth: '600px', maxWidth: '600px' }
};

// Each org lists the mold(s) it has a card for. Orgs with only one mold skip
// the toggle; orgs with both (old + new) show it.
const MOLD_CARD_COMPONENTS = {
  [APP_CONSTANTS.UJN_CLIENT_IDENTIFIER]: { old: UjnScanAndPayCard },
  [APP_CONSTANTS?.IOB_CLIENT_IDENTIFIER]: { old: IobScanAndPayCard, new: IobNewScanAndPayCard },
  [APP_CONSTANTS?.CBI_CLIENT_IDENTIFIER]: { old: CbiScanAndPayCard, new: CbiNewScanAndPayCard },
  [APP_CONSTANTS?.SBI_CLIENT_IDENTIFIER]: { old: SbiScanAndPayCard },
  [APP_CONSTANTS?.COSMOS_CLIENT_IDENTIFIER]: { old: CosmosScanAndPayCard },
  [APP_CONSTANTS?.PRATAP_CLIENT_IDENTIFIER]: { old: PratapScanAndPayCard },
  [APP_CONSTANTS?.RSB_CLIENT_IDENTIFIER]: { old: RsbcanAndPayCard, new: RsbNewScanAndPayCard },
  [APP_CONSTANTS?.GBCB_CLIENT_IDENTIFIER]: { old: GbcbScanAndPayCard },
  [APP_CONSTANTS?.KAGB_CLIENT_IDENTIFIER]: { new: KagbNewScanAndPayCard },
  [APP_CONSTANTS?.HASTI_CLIENT_IDENTIFIER]: { new: HastiNewScanAndPayCard }
};

// New-mold cards are built to fill a fixed-size container (width/height:
// 100%), unlike the old cards which size themselves. These are the capture
// dimensions each card was designed for.
const NEW_MOLD_CARD_SIZE = {
  [APP_CONSTANTS?.IOB_CLIENT_IDENTIFIER]: { width: 350, height: 552 },
  [APP_CONSTANTS?.CBI_CLIENT_IDENTIFIER]: { width: 380, height: 600 },
  [APP_CONSTANTS?.KAGB_CLIENT_IDENTIFIER]: { width: 385, height: 608 },
  // RSB and HASTI have no upstream mold config to derive from - matched to CBI's size.
  [APP_CONSTANTS?.RSB_CLIENT_IDENTIFIER]: { width: 380, height: 600 },
  [APP_CONSTANTS?.HASTI_CLIENT_IDENTIFIER]: { width: 380, height: 600 }
};

export default function ScanAndPayModal({
  qrString,
  merchantVpa,
  open,
  onClose,
  Identifier,
  merchantName
}) {
  const molds = MOLD_CARD_COMPONENTS[Identifier] || { old: UjnScanAndPayCard };
  const hasOld = Boolean(molds.old);
  const hasNew = Boolean(molds.new);
  const showToggle = hasOld && hasNew;

  const [selectedMold, setSelectedMold] = useState(hasNew ? 'new' : 'old');

  // Reset to the org's default mold whenever a different org's card is opened.
  useEffect(() => {
    setSelectedMold(hasNew ? 'new' : 'old');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Identifier, open]);

  const CardComponent = molds[selectedMold] || molds.old || molds.new;
  const { minWidth, maxWidth } = MODAL_WIDTH_CONFIG[Identifier] || MODAL_WIDTH_CONFIG.DEFAULT;

  // The old cards carry their own Download/Share buttons internally. The new
  // mold cards are plain visual components (built to be captured for bulk PDF
  // generation), so the same buttons are provided here instead.
  const newCardRef = useRef(null);

  const generateNewCardPDF = async () => {
    if (!newCardRef.current) return null;

    const canvas = await html2canvas(newCardRef.current, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff'
    });
    const imgData = canvas.toDataURL('image/png');

    const pdf = new JsPDF({
      orientation: 'portrait',
      unit: 'px',
      format: [canvas.width, canvas.height]
    });

    pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
    return pdf;
  };

  const handleDownloadNewCardPDF = async () => {
    const pdf = await generateNewCardPDF();
    if (pdf) pdf.save(`${merchantVpa || 'MerchantCard'}.pdf`);
  };

  const handleShareNewCardPDF = async () => {
    const pdf = await generateNewCardPDF();
    if (!pdf) return;

    const pdfBlob = pdf.output('blob');
    const file = new File([pdfBlob], `${merchantVpa || 'MerchantCard'}.pdf`, {
      type: 'application/pdf'
    });

    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          title: 'Merchant QR Card',
          text: 'Here is my QR card.',
          files: [file]
        });
      } catch (err) {
        console.error('Share failed:', err);
      }
    } else {
      pdf.save(`${merchantVpa || 'MerchantCard'}.pdf`);
      alert('Sharing is not supported on this device/browser. PDF downloaded instead.');
    }
  };

  return (
    <Dialog
      open={open}
      data-testid="scan_and_pay_modal"
      onClose={onClose}
      PaperProps={{
        style: {
          borderRadius: 16,
          minWidth, // minimum width
          maxWidth, // maximum width
          width: 'auto' // fit content
        }
      }}
    >
      <DialogContent sx={{ position: 'relative' }}>
        {/* Close button */}
        <IconButton
          data-testid="scan_and_pay_modal_close_button"
          onClick={onClose}
          sx={{ position: 'absolute', top: 8, right: 8 }}
        >
          <CloseIcon />
        </IconButton>

        {showToggle && (
          <ToggleButtonGroup
            value={selectedMold}
            exclusive
            size="small"
            onChange={(event, value) => value && setSelectedMold(value)}
            data-testid="qr_card_mold_toggle"
            sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}
          >
            <ToggleButton value="new" data-testid="qr_card_mold_toggle_new">
              New Card
            </ToggleButton>
            <ToggleButton value="old" data-testid="qr_card_mold_toggle_old">
              Old Card
            </ToggleButton>
          </ToggleButtonGroup>
        )}

        {selectedMold === 'new' ? (
          <>
            <Box
              ref={newCardRef}
              sx={{ mx: 'auto', ...(NEW_MOLD_CARD_SIZE[Identifier] || { width: 320, height: 505 }) }}
            >
              <CardComponent qrString={qrString} merchantVpa={merchantVpa} merchantName={merchantName} />
            </Box>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleDownloadNewCardPDF}
                endIcon={<DownloadIcon />}
                sx={{ p: 1, width: '50%' }}
              >
                Download
              </Button>
              <Button
                variant="outlined"
                onClick={handleShareNewCardPDF}
                endIcon={<ShareIcon />}
                sx={{ p: 1, width: '50%' }}
              >
                Share
              </Button>
            </Box>
          </>
        ) : (
          <CardComponent qrString={qrString} merchantVpa={merchantVpa} merchantName={merchantName} />
        )}
      </DialogContent>
    </Dialog>
  );
}
